/**
 * HormoneTrendChart.jsx — iter663
 *
 * Time-series trend chart for serum hormones (E2, прогестерон, SHBG, TSH +
 * допълнителни). Reference range bands за всеки маркер. Cycle phase hint.
 * Strict Bulgarian UI.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  LineChart, Line, ResponsiveContainer, CartesianGrid, XAxis, YAxis,
  Tooltip, ReferenceArea, Legend,
} from "recharts";
import { TrendingUp, Loader2, Activity, Calendar } from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";

const HORMONE_META = {
  estradiol_e2_pmol_l:       { label: "Естрадиол (E2)",       unit: "pmol/L", color: "#ec4899", ref: "follicular" },
  progesterone_nmol_l:       { label: "Прогестерон",          unit: "nmol/L", color: "#a855f7", ref: "luteal" },
  shbg_nmol_l:               { label: "SHBG",                 unit: "nmol/L", color: "#3b82f6", ref: "adult_female" },
  tsh_miu_l:                 { label: "TSH",                  unit: "mIU/L",  color: "#10b981", ref: "adult" },
  testosterone_total_nmol_l: { label: "Тестостерон общ",      unit: "nmol/L", color: "#f59e0b", ref: "adult_female" },
  prolactin_miu_l:           { label: "Пролактин",            unit: "mIU/L",  color: "#ef4444", ref: "adult_female" },
  fsh_iu_l:                  { label: "FSH",                  unit: "IU/L",   color: "#8b5cf6", ref: "follicular" },
  lh_iu_l:                   { label: "LH",                   unit: "IU/L",   color: "#06b6d4", ref: "follicular" },
  amh_pmol_l:                { label: "AMH",                  unit: "pmol/L", color: "#84cc16", ref: "adult_female_general" },
  dhea_s_umol_l:             { label: "DHEA-S",               unit: "µmol/L", color: "#f97316", ref: "adult_female_30s" },
  cortisol_morning_nmol_l:   { label: "Кортизол сутр.",       unit: "nmol/L", color: "#eab308", ref: "adult_morning" },
  ft4_pmol_l:                { label: "FT4",                  unit: "pmol/L", color: "#14b8a6", ref: "adult" },
  ft3_pmol_l:                { label: "FT3",                  unit: "pmol/L", color: "#0ea5e9", ref: "adult" },
};

const PHASE_LABELS = {
  luteal:                          { lab: "Лутеална фаза",        emoji: "🌙" },
  late_follicular_ovulatory:       { lab: "Късна фоликуларна / овулация", emoji: "✨" },
  early_follicular_or_menstrual:   { lab: "Ранна фоликуларна / менструация", emoji: "💧" },
  postmenopausal:                  { lab: "Постменопауза",       emoji: "🌅" },
};

function nicelyShortDate(d) {
  if (!d) return "";
  // d is "YYYY-MM-DD"
  const parts = d.split("-");
  if (parts.length !== 3) return d;
  return `${parts[2]}.${parts[1]}.${parts[0].slice(2)}`;
}

export default function HormoneTrendChart({ refreshKey }) {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState("estradiol_e2_pmol_l");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/bhi/hormones/trends`);
      setData(data);
    } catch {
      setData(null);
    } finally { setLoading(false); }
  }, [API]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const availableKeys = useMemo(() => {
    if (!data?.series) return [];
    return Object.entries(data.series)
      .filter(([, pts]) => Array.isArray(pts) && pts.length > 0)
      .map(([k]) => k);
  }, [data]);

  useEffect(() => {
    if (availableKeys.length > 0 && !availableKeys.includes(selected)) {
      setSelected(availableKeys[0]);
    }
  }, [availableKeys, selected]);

  if (loading && !data) {
    return (
      <div className="p-6 text-center" data-testid="hormone-trend-loading">
        <Loader2 className="w-5 h-5 animate-spin mx-auto opacity-60" />
      </div>
    );
  }

  if (!data || availableKeys.length === 0) {
    return (
      <div
        className={`p-5 rounded-xl text-center text-sm ${isDark ? "bg-white/[0.03] text-gray-400" : "bg-gray-50 text-gray-600"}`}
        data-testid="hormone-trend-empty"
      >
        <Activity className="w-7 h-7 mx-auto opacity-40 mb-2" />
        <p>Все още няма данни за посоката. Качете 2 или повече серумни тестове, за да видите динамиката.</p>
      </div>
    );
  }

  const meta = HORMONE_META[selected] || { label: selected, unit: "", color: "#888" };
  const series = (data.series[selected] || []).map((pt) => ({ ...pt, dateShort: nicelyShortDate(pt.date) }));
  const refRangeObj = data.reference_ranges?.[selected];
  const refRange = refRangeObj?.[meta.ref] || Object.values(refRangeObj || {})[0];

  // Determine Y domain padding around data + ref range
  const allYs = [
    ...series.map((s) => s.value),
    ...(Array.isArray(refRange) ? refRange : []),
  ];
  const yMin = Math.min(...allYs) * 0.85;
  const yMax = Math.max(...allYs) * 1.15;

  const phaseInfo = data.cycle_phase_hint ? PHASE_LABELS[data.cycle_phase_hint] : null;

  return (
    <div data-testid="hormone-trend-chart">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center shrink-0">
          <TrendingUp className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <h4 className="font-bold text-base">📈 Посока на серумните хормони</h4>
          <p className={`text-[11px] mt-0.5 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
            Динамика през времето · Сравнение спрямо референтни интервали · Образователен индикатор за фазата на цикъла
          </p>
        </div>
      </div>

      {phaseInfo && (
        <div
          className={`mb-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold ${
            isDark ? "bg-pink-500/15 text-pink-300" : "bg-pink-100 text-pink-700"
          }`}
          data-testid="cycle-phase-hint"
        >
          <Calendar className="w-3.5 h-3.5" />
          {phaseInfo.emoji} {phaseInfo.lab}
        </div>
      )}

      {/* Hormone selector */}
      <div className="flex flex-wrap gap-1.5 mb-3" data-testid="hormone-selector">
        {availableKeys.map((k) => {
          const m = HORMONE_META[k] || { label: k };
          const active = k === selected;
          return (
            <button
              key={k}
              onClick={() => setSelected(k)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${
                active
                  ? "bg-gradient-to-r from-pink-500 to-purple-500 text-white"
                  : (isDark ? "bg-white/5 text-gray-300 hover:bg-white/10" : "bg-gray-100 text-gray-700 hover:bg-gray-200")
              }`}
              data-testid={`hormone-pill-${k}`}
            >
              {m.label}
            </button>
          );
        })}
      </div>

      {/* Chart */}
      <div className={`p-3 rounded-xl ${isDark ? "bg-white/[0.03]" : "bg-gray-50/60"}`}>
        <div className="flex items-baseline justify-between mb-1">
          <div className="text-sm font-bold" style={{ color: meta.color }}>
            {meta.label} <span className="text-[11px] opacity-60 font-normal">({meta.unit})</span>
          </div>
          <div className="text-[10px] opacity-60">{series.length} измерване{series.length === 1 ? "" : "ия"}</div>
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={series} margin={{ top: 8, right: 16, bottom: 4, left: -8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"} />
            <XAxis dataKey="dateShort" tick={{ fontSize: 10, fill: isDark ? "#cbd5e1" : "#475569" }} />
            <YAxis domain={[yMin, yMax]} tick={{ fontSize: 10, fill: isDark ? "#cbd5e1" : "#475569" }} />
            {Array.isArray(refRange) && refRange.length === 2 && (
              <ReferenceArea
                y1={refRange[0]}
                y2={refRange[1]}
                fill={meta.color}
                fillOpacity={0.08}
                stroke="none"
              />
            )}
            <Tooltip
              contentStyle={{
                background: isDark ? "#0f172a" : "#fff",
                border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#e5e7eb"}`,
                borderRadius: 8,
                fontSize: 12,
              }}
              labelStyle={{ color: isDark ? "#e2e8f0" : "#1a1a2e", fontWeight: 700 }}
              formatter={(value) => [`${value} ${meta.unit}`, meta.label]}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke={meta.color}
              strokeWidth={2.5}
              dot={{ r: 4, fill: meta.color, strokeWidth: 0 }}
              activeDot={{ r: 6 }}
            />
            <Legend wrapperStyle={{ display: "none" }} />
          </LineChart>
        </ResponsiveContainer>
        {Array.isArray(refRange) && refRange.length === 2 && (
          <p className="text-[10px] opacity-60 mt-1 italic text-center">
            Засенчената лента показва референтния интервал ({refRange[0]}–{refRange[1]} {meta.unit}) — образователно, варира между лаборатории.
          </p>
        )}
      </div>
    </div>
  );
}
